import React from 'react';
import { useState } from 'react';
import { ClientOrderQuote } from '../../sp-types/clientOrderQuoteType';
import { DistributeValueThroughDates } from '../../utils/MonthYear/distributeValueThroughDates/distributeValueThroughDates';
import { ClientOrderCompleteQuote } from './ScreenComponents/ClientOrderPages/ClientOrderCompleteQuote/ClientOrderCompleteQuote';
import { CoPreviewContract } from './ScreenComponents/ClientOrderPages/ClientOrderCompleteQuote/PreviewContract/ClientOrderPreviewContract';
import { ContractButtonSection } from './ScreenComponents/ClientOrderPages/ClientOrderCompleteQuote/PreviewContract/ContractButtonSection';
import { ClientOrderRefineQuote } from './ScreenComponents/ClientOrderPages/ClientOrderCompleteQuote/RefineQuote/ClientOrderRefineQuote';
import { CoRefineButtonSection } from './ScreenComponents/ClientOrderPages/ClientOrderCompleteQuote/RefineQuote/CoRefineButtonSection';
import { ClientOrderScreen } from './ScreenComponents/ClientOrderPages/ClientOrderScreen';
import { CoCompleteOrder } from './ScreenComponents/ClientOrderPages/CompleteOrder/ClientOrderCompleteOrder';
import { ClientOrderCreateDrag } from './ScreenComponents/ClientOrderPages/CreateOrderDragFile/ClientOrderCreateDrag';
import { CoQuoteDueDate } from './ScreenComponents/ClientOrderPages/DueDateCalendar/ClientOrderQuoteDueDate';
import { CoQuoteDueDateButtonSection } from './ScreenComponents/ClientOrderPages/DueDateCalendar/DuoDateComponents/CoQuoteDueDateButtonSection';
import { OrderSummary } from './ScreenComponents/OrderSummary';

export const NewClientOrder = () => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isExposureLoading, setIsExposureLoading] = useState(false);
  const [updateRefineSection, setUpdateRefineSection] = useState(false);
  const [clientOrder, setClientOrder] = useState<ClientOrderQuote>({
    contractType: undefined,
    DueDate: undefined,
    pricePerUnit: 0,
    productName: '',
    quantity: 0,
    priceCalendar: DistributeValueThroughDates(500, { month: 1, year: 2025 }),
  });

  const updateClientOrder = <K extends keyof ClientOrderQuote>(prop: K, value: ClientOrderQuote[K]) => {
    setClientOrder((prevState) => ({
      ...prevState,
      [prop]: value,
    }));
  };

  const componentDictionary: {
    [key: number]: IClientOrderPage<React.FC<any>, React.FC<any> | undefined>;
  } = {
    0: {
      component: ClientOrderCreateDrag,
      componentProps: {
        setNextPage: setCurrentPageIndex,
        setClientOrderState: setClientOrder,
        onFilesChanged: () => 0,
        fileFormatAllowed: [],
      } as InferProps<typeof ClientOrderCreateDrag>,
      id: '1.',
      title: 'Create your quote',
      description: 'Create a custom future quote',
    },

    1: {
      component: CoQuoteDueDate,
      componentProps: {
        duoDate: clientOrder.DueDate,
        updateClientOrder: updateClientOrder,
        priceCalender: clientOrder.priceCalendar,
        quantity: clientOrder.quantity,
      } as InferProps<typeof CoQuoteDueDate>,
      id: '3.',
      title: 'Set delivery dates',
      description: 'Select future deliveries ',
      buttonSectionComponent: CoQuoteDueDateButtonSection,
      buttonSectionComponentProps: {
        nextPageFunction: () => {
          setCurrentPageIndex(currentPageIndex + 1);
        },
        backPageFunction: () => {
          setCurrentPageIndex(currentPageIndex - 1);
        },
        refineFunction: () => {
          alert('refine');
        },
        disableButton: !clientOrder.DateRange,
      } as InferProps<typeof CoQuoteDueDateButtonSection>,
    },
    2: {
      component: ClientOrderCompleteQuote,
      componentProps: {
        clientOrder: clientOrder,
        navigateToHandShake: () => {
          setCurrentPageIndex(5);
        },
        navigateToLegal: () => {
          setCurrentPageIndex(4);
        },
        navigateToRefine: () => {
          setCurrentPageIndex(4);
        },
        setIsExposureLoading: setIsExposureLoading,
      } as InferProps<typeof ClientOrderCompleteQuote>,
      title: 'Complete your quote',
      description: 'Review, refine and approve your quote',
    },
    3: {
      component: CoPreviewContract,
      componentProps: {
        duoDate: clientOrder.DueDate,
        updateClientOrder: updateClientOrder,
        priceCalender: clientOrder.priceCalendar,
        quantity: clientOrder.quantity,
      },
      buttonSectionComponent: ContractButtonSection,
      buttonSectionComponentProps: {
        onCloseDocument: () => {
          setCurrentPageIndex(2);
        },
      } as InferProps<typeof ContractButtonSection>,
    },
    4: {
      component: ClientOrderRefineQuote,
      componentProps: {
        clientOrder: clientOrder,
        updateClientOrder: updateClientOrder,
        setUpdateParentState: setUpdateRefineSection,
        updateParentState: updateRefineSection,
      } as InferProps<typeof ClientOrderRefineQuote>,
      title: 'Refine your quote',
      description: 'Fill in and adjust order parameters to receive updated future fixed-price quote ',
      buttonSectionComponent: CoRefineButtonSection,
      buttonSectionComponentProps: {
        cancelFunction: () => {
          setCurrentPageIndex(2);
        },
        updateDataFunction: async () => {
          setUpdateRefineSection(true);
        },
      } as InferProps<typeof CoRefineButtonSection>,
    },
    5: {
      component: CoCompleteOrder,
      componentProps: { isLoading: isExposureLoading } as InferProps<typeof CoCompleteOrder>,
    },
  };

  const currentEntry = componentDictionary[currentPageIndex];
  const CurrentComponent = currentEntry.component;
  const CurrentButtonSection = currentEntry.buttonSectionComponent;

  return (
    <div className="flex flex-1 h-full  xl:flex-row lg:flex-row sm:flex-col-reverse md:flex-col-reverse  py-2  lg:space-x-[1.25rem] xl:space-x-[1.25rem]    ">
      <div className="flex px-[1.5rem] pb-[1rem] pt-[1.5rem] basis-4/5 bg-white shadow-custom sm:mt-[1.25rem] md:mt-[1.25rem] lg:mt-[0]  ">
        <ClientOrderScreen
          title={currentEntry.title}
          description={currentEntry.description}
          id={currentEntry.id?.toString()}
          buttonSection={CurrentButtonSection && <CurrentButtonSection {...currentEntry.buttonSectionComponentProps} />}
        >
          <CurrentComponent {...currentEntry.componentProps} />
        </ClientOrderScreen>
      </div>
      <div className="basis-1/5 max-h-full ">
        <OrderSummary
          setIsLoadingExposure={setIsExposureLoading}
          clientOrder={clientOrder}
          setCurrentPage={setCurrentPageIndex}
          currentPageIndex={currentPageIndex}
        />
      </div>
    </div>
  );
};

type InferProps<T> = T extends React.FC<infer Props> ? Props : never;

interface IClientOrderPage<C, B = undefined> {
  title?: string;
  description?: string;
  id?: string | number;
  component: C;
  componentProps: InferProps<C>;
  buttonSectionComponent?: B;
  buttonSectionComponentProps?: InferProps<B>;
}
